import { combineReducers, createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { normalize, schema } from 'normalizr';
import { createTableSlice, TableSettingsColumn } from '../../../common/datagrid';
import {
    ExternalPriceDto,
    GetExternalPricePartialDto,
    GetPricesByExternalPricelistsDto,
    PriceDto,
    PricelistPeriodDto,
    TableType
} from '../../../common/PricePrgClient';
import { createAsyncAction, deletePropertiesEndingWith, distinct, toDateString } from '../../../utils';
import { fetchProducts } from '../../product/redux/productSlices';
import { fetchUserSettings } from '../../settings/redux/settingsSlices';
import { ExternalPrice } from '../externalPriceTypes';

const namespaces = {
    externalPrices: 'externalPrices',
    externalPriceSource: 'externalPriceSource',
    selectedExternalPricelistIds: 'selectedExternalPricelistIds',
    externalPricelistPeriods: 'externalPricelistPeriods',
    externalPriceSelectedDate: 'externalPriceSelectedDate',
    externalPriceDialogs: 'externalPriceDialogs',
    isLoading: 'isLoading',
    showAllArticles: 'showAllArticles',
    isExporting: 'isExporting'
};

type FetchExternalPricesRequest = { date: string; pricelistIds: number[] };
type UpdateExternalPriceRequest = { extPriceId: number; newPrice: number };
type SaveExternalPriceCommentRequest = { extPriceId: number; comment: string | null };
type SaveExternalPriceCommentSuccess = { extPriceIds: number[]; comment: string | null };
type SaveExternalPriceDiscountRequest = { extPriceId: number; discount: number | null };
type SaveExternalPriceDiscountSuccess = SaveExternalPriceDiscountRequest;

export const getExternalPriceKey = ({
    externalPricelistId,
    productId
}: Pick<ExternalPrice, 'externalPricelistId' | 'productId'>) => `${externalPricelistId}-${productId}`;
const externalPricesEntity = new schema.Entity<ExternalPrice>('externalPrices', undefined, {
    idAttribute: getExternalPriceKey
});

export const fetchExternalPrices = createAsyncAction<FetchExternalPricesRequest, ExternalPriceDto[]>(
    namespaces.externalPrices,
    'fetchExternalPrices'
);
export const fetchExternalPricesByIds = createAsyncAction<number[], ExternalPriceDto[]>(
    namespaces.externalPrices,
    'fetchExternalPricesByIds'
);
export const fetchExternalPricesPartial = createAsyncAction<GetExternalPricePartialDto, ExternalPriceDto[]>(
    namespaces.externalPrices,
    'fetchExternalPricesPartial'
);
export const updateExternalPrice = createAsyncAction<UpdateExternalPriceRequest, ExternalPriceDto>(
    namespaces.externalPrices,
    'updateExternalPrice'
);
export const saveExternalPriceComment = createAsyncAction<
    SaveExternalPriceCommentRequest,
    SaveExternalPriceCommentSuccess
>(namespaces.externalPrices, 'saveExternalPriceComment');
export const saveExternalPriceDiscount = createAsyncAction<
    SaveExternalPriceDiscountRequest,
    SaveExternalPriceDiscountSuccess
>(namespaces.externalPrices, 'saveExternalPriceDiscount');

const externalPriceSlice = createSlice({
    name: namespaces.externalPrices,
    initialState: {} as { [externalPriceId: string]: ExternalPrice },
    reducers: {
        cancelExternalPriceUpdate: (state, action: PayloadAction<number>) => {
            const oldPrice = Object.values(state).find(x => x.externalPriceId === action.payload);
            if (oldPrice != null) {
                delete oldPrice.priceToBeSaved;
            }
        },
        removeExternalPrices: (
            state,
            action: PayloadAction<{ productId: number; externalPricelistId: number }[]>
        ) => {
            for (const deletedExtPrice of action.payload) {
                delete state[getExternalPriceKey(deletedExtPrice)];
            }
        }
    },
    extraReducers: builder =>
        builder
            .addCase(fetchExternalPrices.success, (state, action) => {
                const normalized = normalize(action.payload, [externalPricesEntity]);
                return normalized.entities.externalPrices ?? {};
            })
            .addCase(fetchExternalPricesByIds.success, (state, action) => {
                for (const price of action.payload) {
                    const oldPrice = state[getExternalPriceKey(price)];
                    if (oldPrice != null) {
                        oldPrice.updatedBy = price.updatedBy;
                        oldPrice.updatedDate = price.updatedDate;
                        oldPrice.priceChangeDirection = price.priceChangeDirection;
                        oldPrice.salesPrice = price.salesPrice;
                        oldPrice.discount = price.discount;
                        oldPrice.comment = price.comment;
                        oldPrice.agreementPriceIds = price.agreementPriceIds;
                    }
                }
            })
            .addCase(fetchExternalPricesPartial.success, (state, action) => {
                for (const price of action.payload) {
                    state[getExternalPriceKey(price)] = price;
                }
            })
            .addCase(updateExternalPrice.request, (state, action) => {
                const { extPriceId, newPrice } = action.payload;

                const oldPrice = Object.values(state).find(x => x.externalPriceId === extPriceId);
                if (oldPrice != null) {
                    oldPrice.priceToBeSaved = newPrice;
                }
            })
            .addCase(updateExternalPrice.success, (state, action) => {
                const oldPrice = state[getExternalPriceKey(action.payload)];
                if (oldPrice != null) {
                    oldPrice.updatedBy = action.payload.updatedBy;
                    oldPrice.updatedDate = action.payload.updatedDate;
                    oldPrice.priceChangeDirection = action.payload.priceChangeDirection;
                    oldPrice.salesPrice = action.payload.salesPrice;
                    oldPrice.comment = action.payload.comment;
                    oldPrice.discount = action.payload.discount;
                    if (action.payload.agreementPriceIds != null) {
                        oldPrice.agreementPriceIds = action.payload.agreementPriceIds;
                    }
                    delete oldPrice.priceToBeSaved;
                }
            })
            .addCase(saveExternalPriceComment.success, (state, action) => {
                const { extPriceIds, comment } = action.payload;

                for (const extPriceId of extPriceIds) {
                    for (const price of Object.values(state)) {
                        if (price.externalPriceId === extPriceId) {
                            if (!comment) {
                                delete price.comment;
                            } else {
                                price.comment = comment;
                            }
                        }
                    }
                }
            })
            .addCase(saveExternalPriceDiscount.success, (state, action) => {
                const { extPriceId, discount } = action.payload;

                for (const price of Object.values(state)) {
                    if (price.externalPriceId === extPriceId) {
                        if (!discount) {
                            delete price.discount;
                        } else {
                            price.discount = discount;
                        }
                    }
                }
            })
            .addCase(fetchProducts.success, (state, action) => {
                const oldProductIds = Object.keys(state)
                    .map(x => Number(x.split('-')[1]))
                    .filter(distinct);

                const newProductIds = new Set(action.payload.map(x => x.productId));

                for (const oldProductId of oldProductIds) {
                    if (!newProductIds.has(oldProductId)) {
                        deletePropertiesEndingWith('-' + String(oldProductId), state);
                    }
                }
            })
});

export const { cancelExternalPriceUpdate, removeExternalPrices } = externalPriceSlice.actions;

const externalPriceSourceEntity = new schema.Entity<PriceDto>('externalPriceSources', undefined, {
    idAttribute: 'priceId'
});

export const fetchExternalPriceSource = createAsyncAction<number[], PriceDto[]>(
    namespaces.externalPriceSource,
    'fetchExternalPriceSource'
);
export const fetchExternalPriceSourcesPartial = createAsyncAction<
    GetPricesByExternalPricelistsDto,
    PriceDto[]
>(namespaces.externalPriceSource, 'fetchExternalPriceSourcesPartial');

const externalPriceSourcesSlice = createSlice({
    name: namespaces.externalPriceSource,
    initialState: {} as { [priceId: string]: PriceDto },
    reducers: {
        removeExternalPricesSources: (
            state,
            action: PayloadAction<{ productId: number; pricelistId: number }[]>
        ) => {
            const oldPrices = Object.values(state);
            for (const removedPrice of action.payload) {
                const oldPrice = oldPrices.find(
                    op =>
                        op.productId === removedPrice.productId && op.pricelistId === removedPrice.pricelistId
                );

                if (oldPrice?.priceId != null) {
                    delete state[oldPrice.priceId];
                }
            }
        }
    },
    extraReducers: builder =>
        builder
            .addCase(fetchExternalPriceSource.success, (state, action) => {
                const normalized = normalize(action.payload, [externalPriceSourceEntity]);
                return normalized.entities.externalPriceSources ?? {};
            })
            .addCase(fetchExternalPriceSourcesPartial.success, (state, action) => {
                const prices = action.payload;

                for (const price of prices) {
                    if (price.priceId != null) {
                        if (price.price === 0) {
                            delete state[price.priceId];
                        } else {
                            state[price.priceId] = price;
                        }
                    }
                }
            })
});

export const { removeExternalPricesSources } = externalPriceSourcesSlice.actions;

const selectedExternalPricelistIdsSlice = createSlice({
    name: namespaces.selectedExternalPricelistIds,
    initialState: [] as number[],
    reducers: {
        externalPriceTablePricelistsSelected: (state, action: PayloadAction<number[]>) => action.payload
    },
    extraReducers: builder =>
        builder.addCase(fetchUserSettings.success, (state, { payload }) => {
            const tableSettings = payload?.tableSettings?.find(ts => ts.table === TableType.ExternalPrice);
            if (tableSettings != null) {
                return tableSettings.tableColumnSettings
                    .filter(tcs => !initialExternalPriceTableColumns.some(iptc => iptc.key === tcs.name))
                    .map(tcs => Number(tcs.name))
                    .filter(num => !isNaN(num));
            }
            return state;
        })
});

export const { externalPriceTablePricelistsSelected } = selectedExternalPricelistIdsSlice.actions;

const externalPricelistPeriodEntity = new schema.Entity<PricelistPeriodDto>(
    'externalPricelistPeriods',
    undefined,
    {
        idAttribute: 'pricelistPeriodId'
    }
);

type FetchExternalPricelistPeriodsRequest = { extPricelistIds: number[]; preselectDate: boolean };
type FetchExternalPricelistPeriodsSuccess = {
    pricelistPeriods: PricelistPeriodDto[];
    preselectDate: boolean;
};

export const fetchExternalPricelistPeriods = createAsyncAction<
    FetchExternalPricelistPeriodsRequest,
    FetchExternalPricelistPeriodsSuccess
>(namespaces.externalPricelistPeriods, 'fetchExternalPricelistPeriods');

const pricelistPeriodsSlice = createSlice({
    name: namespaces.externalPricelistPeriods,
    initialState: {} as { [pricelistPeriodId: string]: PricelistPeriodDto },
    reducers: {},
    extraReducers: builder =>
        builder.addCase(fetchExternalPricelistPeriods.success, (state, action) => {
            const normalized = normalize(action.payload.pricelistPeriods, [externalPricelistPeriodEntity]);
            return normalized.entities.externalPricelistPeriods ?? {};
        })
});

const selectedExternalPriceDateSlice = createSlice({
    name: namespaces.externalPriceSelectedDate,
    initialState: null as string | null,
    reducers: {
        externalPricelistDateSelected: (state, action: PayloadAction<string>) => toDateString(action.payload)
    }
});
export const { externalPricelistDateSelected } = selectedExternalPriceDateSlice.actions;

const initialExternalPriceTableColumns = ['productId', 'productExtRef', 'productName', 'unit', 'country'].map(
    key => ({
        key: key,
        hide: false
    })
);

export const pricelistColumnsSelected = createAction<{
    inPriceColumnIds: { [inPriceColumnId: string]: number[] };
    externalPriceColumnIds: number[];
}>('@externalPricetable/columns');

const externalPriceTableSlice = createTableSlice(
    'externalPriceTable',
    {
        table: TableType.ExternalPrice,
        filters: {
            productId: '',
            productExtRef: '',
            productName: '',
            unit: '',
            country: ''
        },
        showTooltips: true,
        sorting: {
            column: 'productId',
            direction: 'asc'
        },
        columns: initialExternalPriceTableColumns
    },
    builder =>
        builder.addCase(pricelistColumnsSelected, (state, action) => {
            const selectedExternalPricelistIds = action.payload;
            for (const externalPricelistId of selectedExternalPricelistIds.externalPriceColumnIds) {
                if (state.filters[externalPricelistId] == null) {
                    state.filters[externalPricelistId] = '';
                }
            }
            for (const inPricelistId of Object.keys(selectedExternalPricelistIds.inPriceColumnIds)) {
                if (state.filters['inPrice-' + inPricelistId] == null) {
                    state.filters['inPrice-' + inPricelistId] = '';
                }
            }

            state.columns = [
                ...initialExternalPriceTableColumns,
                ...Object.keys(selectedExternalPricelistIds.inPriceColumnIds).flatMap(s => {
                    const columns: TableSettingsColumn[] = [];
                    columns.push({
                        key: String('inPrice-' + s),
                        hide: false
                    });
                    const externalPriceColumns = selectedExternalPricelistIds.inPriceColumnIds[s].flatMap(
                        id => [
                            {
                                key: id.toString(),
                                hide: false
                            },
                            { key: 'margin-' + id, hide: false }
                        ]
                    );
                    return columns.concat(externalPriceColumns);
                })
            ].map(col => {
                const oldCol = state.columns.find(c => col.key === c.key);
                if (oldCol == null) {
                    return col;
                }

                return {
                    ...col,
                    ...oldCol
                };
            });
        })
);
export const { externalPriceTableActions } = externalPriceTableSlice;

const externalPriceDialogsSlice = createSlice({
    name: namespaces.externalPriceDialogs,
    initialState: {
        isChooseExternalPricelistsDialogOpen: false
    },
    reducers: {
        openChooseExternalPricelistsDialog: state => {
            state.isChooseExternalPricelistsDialogOpen = true;
        },
        closeChooseExternalPricelistsDialog: state => {
            state.isChooseExternalPricelistsDialogOpen = false;
        }
    }
});

export const { closeChooseExternalPricelistsDialog, openChooseExternalPricelistsDialog } =
    externalPriceDialogsSlice.actions;

const isLoadingSlice = createSlice({
    name: namespaces.isLoading,
    initialState: false,
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(fetchExternalPrices.request, () => true)
            .addCase(fetchExternalPrices.success, () => false)
});
const isExportingSlice = createSlice({
    name: namespaces.isExporting,
    initialState: false,
    reducers: {
        setIsExporting: (state, action: PayloadAction<boolean>) => action.payload
    }
});

export const { setIsExporting } = isExportingSlice.actions;
const checkboxFiltersSlice = createSlice({
    name: namespaces.showAllArticles,
    initialState: {
        showAllArticles: false,
        showCommentsOnly: false,
        showNewArticlesOnly: false
    },
    reducers: {
        showAllArticlesChanged: (state, action: PayloadAction<boolean>) => {
            state.showAllArticles = action.payload;
        },
        showCommentsOnlyChanged: (state, action: PayloadAction<boolean>) => {
            state.showCommentsOnly = action.payload;
        },
        showNewArticlesOnlyChanged: (state, action: PayloadAction<boolean>) => {
            state.showNewArticlesOnly = action.payload;
        }
    }
});

export const { showAllArticlesChanged, showCommentsOnlyChanged, showNewArticlesOnlyChanged } =
    checkboxFiltersSlice.actions;

export default combineReducers({
    selectedExternalPricelistIds: selectedExternalPricelistIdsSlice.reducer,
    selectedExternalPricelistPeriods: pricelistPeriodsSlice.reducer,
    externalPrices: externalPriceSlice.reducer,
    selectedExternalPriceDate: selectedExternalPriceDateSlice.reducer,
    externalPriceSource: externalPriceSourcesSlice.reducer,
    ui: combineReducers({
        externalPriceTable: externalPriceTableSlice.reducer,
        externalPricelistDialog: externalPriceDialogsSlice.reducer,
        checkboxFilters: checkboxFiltersSlice.reducer,
        isLoading: isLoadingSlice.reducer,
        isExporting: isExportingSlice.reducer
    })
});
